import { Fragment, useEffect } from 'react'
import { katalog, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import {
  achsenAusPfad,
  ebene,
  istGrundachse,
  istNichtZugeordnet,
  markertext,
  menuetext,
  moeglicheAchsen,
  offeneAchsen,
  VORGABE,
  zellenname,
  zellenschluessel,
} from './Aufriss'
import type { Achse, Achsenangabe, Zelle } from './Aufriss'
import { AUFRISSSTUFE, einzug as einzugTiefe, Namenszeile, trefferklasse } from './Bericht'
import type { Aufrissbedienung, Trefferzustand } from './Bericht'
import { istLeereZelle, unterdrueckt, useAnzeigeoptionen } from './Anzeigeoptionen'
import { zeilenId } from './Suche'

import { Zeilenmenue } from './Zeilenmenue'
import type { Menuepunkt } from './Zeilenmenue'

/**
 * Der aufgeklappte Baum unter einer Berichtszeile — für alle Spaltenbilder
 * und alle Berichte mit zeilenweiser Kontenstruktur derselbe.
 *
 * Was sich zwischen den Bildern unterscheidet, sind die **Zellen einer Zeile**,
 * nicht ihr Aufbau. Deshalb bekommt dieses Bauteil die Wertspalten als
 * Funktion herein: Der Konsolidierungsbericht gibt sieben Zahlen, die
 * Konzernstruktur eine je Gesellschaft, die Periodenansicht eine je Monat.
 *
 * **Zwei Sorten Ebenen.** Sachkonto und Gesellschaft stehen im Bericht und
 * werden aus seinen Zellen geschnitten. Partner, Segment und die
 * Analysedimensionen werden **auf Abruf** geholt — die Ebene sagt „wird
 * geladen", bis die Zellen da sind, und danach ist sie eine Ebene wie jede
 * andere: Unter einer Kostenstelle geht es weiter nach Gesellschaften, weil
 * der Abruf die Kreuzzellen mitbringt (R212).
 */

/** Was für eine Zeile offen ist: die Achsen, in dieser Reihenfolge. */
export type Aufrisszustand = Map<string, Achse>

/**
 * Woher ein Bericht die Zellen einer Zusatzachse holt.
 *
 * Jeder Bericht mit zeilenweiser Kontenstruktur stellt eine bereit — Bilanz,
 * GuV, Kapitalflussrechnung, die Spiegel — und liefert die Zellen **in seiner
 * eigenen Form**, mit den Werten seiner Spalten. Der Baum hier weiß nur, dass
 * sie Zellen sind.
 */
export type Achsenquelle<T extends Zelle = Zelle> = {
  /** Eindeutig je Bericht und Lauf — der Schlüssel des Zwischenspeichers. */
  kennung: string
  /** Welche Zusatzachsen dieser Bericht anbietet. */
  angebot: Achsenangabe[]
  /** Die Zellen einer Ebene holen — mit dem Filter, den der Pfad festlegt. */
  lade: (position: string, pfad: Zelle[], achse: Achse) => Promise<T[]>
}

/** Der Stand einer nachgeladenen Ebene. */
export type Ebenenstand<T extends Zelle = Zelle> =
  | { zustand: 'laedt' }
  /**
   * `satz`: der fachliche Satz zum Abbruch; `null` bei einer Störung — die
   * meldet das Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
   */
  | { zustand: 'fehler'; satz?: string | null }
  | { zustand: 'bereit'; zellen: T[] }

/**
 * Woran die Zelle hing, auf der das Menü geöffnet wurde: die Spalte und —
 * im Periodenbild — ihre Periode. Der Wertenachweis aus dem Menü erbt sie,
 * so wie der Doppelklick sie erbt (R205, R213).
 */
export type Zellkontext = {
  spalte?: string
  periode?: number
  von?: number
  bis?: number
  spiegelblock?: string
  spiegelspalte?: string
}

export type Menueruf = (
  x: number,
  y: number,
  position: string,
  eltern: Zelle[],
  zelle: Zelle,
  kontext: Zellkontext,
) => void

/**
 * Der Schlüssel, unter dem der Zustand einer Ebene steht: Position und der
 * Pfad dorthin. Ohne die Position im Schlüssel teilten sich zwei Zeilen mit
 * demselben Konto ihren Aufklappzustand.
 */
export function ebenenschluessel(position: string, eltern: Zelle[]): string {
  return [position, ...eltern.map(zellenschluessel)].join('>')
}

/** Die Zusatzachsen, die ein Bericht wirklich anbietet: eingerichtet und belegt. */
export function zusatzachsen(quelle: Achsenquelle | undefined): Achse[] {
  return (quelle?.angebot ?? []).filter((a) => a.belegt).map((a) => a.schluessel)
}

/**
 * Woran die angeklickte Zelle hing — gelesen von der Zelle selbst, nicht
 * geraten (R213). Mit der Tastatur geöffnet gibt es keine Zelle; dann gilt
 * die Zeile.
 */
export function zellkontext(e: React.MouseEvent): Zellkontext {
  const zelle = (e.target as HTMLElement | null)?.closest?.('td') as HTMLTableCellElement | null
  if (!zelle?.dataset) {
    return {}
  }

  const d = zelle.dataset
  return {
    spalte: d.spalte,
    periode: d.periode ? Number(d.periode) : undefined,
    von: d.von ? Number(d.von) : undefined,
    bis: d.bis ? Number(d.bis) : undefined,
    spiegelblock: d.spiegelblock,
    spiegelspalte: d.spiegelspalte,
  }
}

/**
 * Wo das Menü aufgeht: am Mauszeiger — und bei Tastaturbedienung, wo der
 * Browser −1 liefert, **an der Zeile** statt in der linken oberen Ecke (R166).
 */
export function menueort(e: React.MouseEvent, zeile: HTMLElement): { x: number; y: number } {
  const kasten = zeile.getBoundingClientRect()
  return {
    x: e.clientX > 0 ? e.clientX : kasten.left + 40,
    y: e.clientY > 0 ? e.clientY : kasten.bottom,
  }
}

/**
 * Der Knopf am Zeilenanfang — der dritte Weg ins Menü (R166), für Touch und
 * für alle, die den Rechtsklick nicht kennen. Blass, bis die Zeile überfahren
 * oder fokussiert wird.
 */
export function Menueknopf({ aufMenue }: { aufMenue: (x: number, y: number) => void }) {
  return (
    <button
      type="button"
      className="zeilenknopf"
      aria-label={t('bericht.menueknopf')}
      title={t('bericht.menueknopf')}
      aria-haspopup="menu"
      onClick={(e) => {
        e.stopPropagation()
        const k = e.currentTarget.getBoundingClientRect()
        aufMenue(k.left, k.bottom + 2)
      }}
    >
      ⋯
    </button>
  )
}

/**
 * **Der Platz des Dreiecks, wo es nichts aufzuklappen gibt** (7.3 Abschnitt
 * 22, R1000).
 *
 * Der Befund vom 2026-10-03: *„Die Positionen dürfen nicht unterschiedlich
 * eingerückt sein — egal ob Konten darunter oder nicht."* Mit dem Dreieck fiel
 * bis dahin auch sein Platz weg, und *1.c) Übrige Umsatzerlöse* begann weiter
 * links als *1.a)* daneben. Unsichtbar, aber mit demselben Zeichen in derselben
 * Klasse — so ist er genau so breit wie das Dreieck, ohne daß eine Zahl im
 * Gestaltungsblatt zweimal gepflegt werden müßte.
 */
export function Pfeilplatz() {
  return (
    <span className="menueknopf pfeilplatz" aria-hidden="true">
      ▸
    </span>
  )
}

/**
 * **Der Doppelklick auf eine Zahl ohne Konto betätigt den Pfeil ihrer Zeile**
 * (7.3 Abschnitt 27, R1032).
 *
 * Gesucht wird der Pfeil, nicht nachgebaut, was er tut: Eine Summenposition
 * klappt ihre Unterpositionen auf, eine bebuchte Position ihre Konten, eine
 * Gesellschaft im Aufriss die nächste Stufe — und der Pfeil weiß jeweils
 * schon, welche. Ein neuer Bericht mit demselben Zeilenanfang ist damit ohne
 * Zutun dabei. Wo kein Pfeil steht, geschieht nichts.
 */
export function zeilenpfeilBetaetigen(zelle: HTMLElement) {
  zelle.closest('tr')?.querySelector<HTMLButtonElement>('button[data-zeilenpfeil]')?.click()
}

/** Stößt das Nachladen einer Ebene an, sobald sie gezeichnet wird. */
function Anforderung({ anfordern }: { anfordern: () => void }) {
  useEffect(() => {
    anfordern()
  }, [anfordern])

  return null
}

export function Aufrisszeilen<T extends Zelle>({
  position,
  ebeneTiefe,
  zellen,
  eltern,
  aufriss,
  quelle,
  vorgabe,
  spaltenzahl,
  treffer,
  aufMenue,
  werteSpalten,
  vorspalte,
  summe = false,
  fest,
}: {
  /**
   * Ob die Zeile eine Summe ist — dann auf keiner Stufe nach Sachkonten
   * (R1235): Unter *Gesamtleistung › Holding AG* stünde ein Konto sonst ein
   * zweites Mal, nach dem unter seiner eigenen Position.
   */
  summe?: boolean
  /**
   * Eine **feste Achse dieser Stufe**, unabhängig vom Aufklappzustand — für
   * die eigenen Konten einer Position mit Unterpositionen (R1621). Sie stehen,
   * solange die Gliederung offen ist, und brauchen keinen eigenen Pfeil. Die
   * Stufen darunter folgen wieder dem Zustand. Ohne Zeile bleibt die Stufe
   * stumm: Eine Position ohne gebuchtes eigenes Konto zeigt keinen
   * Leerhinweis zwischen sich und ihren Unterpositionen.
   */
  fest?: Achse
  position: string
  /** Einzugstiefe der Elternzeile — die Kinder rücken je Stufe weiter ein. */
  ebeneTiefe: number
  /**
   * Der Zellenvorrat dieser Ebene: die Zellen des Berichts — oder, unter
   * einer nachgeladenen Zelle, die Zellen ihres Abrufs samt Kreuzzellen.
   */
  zellen: T[]
  eltern: Zelle[]
  aufriss: Aufrissbedienung
  /** Woher die Zusatzachsen kommen — ohne Quelle gibt es nur die Grundachsen. */
  quelle?: Achsenquelle<T>
  vorgabe: Achse
  spaltenzahl: number
  /** Welche Zeilen die Suche gefunden hat. */
  treffer?: Trefferzustand
  aufMenue: Menueruf
  /**
   * Die Wertzellen einer Aufrisszeile — je Spaltenbild andere.
   *
   * Die Zeilenkennung kommt mit, weil das Spaltenbild sonst nicht wüsste,
   * welche seiner Zellen die Suche getroffen hat.
   */
  werteSpalten: (zelle: T, id: string, pfad: Zelle[]) => React.ReactNode
  /**
   * Eine Zelle **vor** der Bezeichnung — für Tabellen, deren erste Spalte
   * nicht die Bezeichnung ist.
   *
   * Die Kapitalflussrechnung hat eine solche: Ihre erste Spalte trägt die
   * Zeilennummer des Standards. Ohne diesen Platzhalter rutschte jede
   * Aufrisszeile um eine Spalte nach links, und der Betrag stünde unter der
   * Bezeichnung statt unter dem Betrag.
   */
  vorspalte?: React.ReactNode
}) {
  const optionen = useAnzeigeoptionen()
  const schluessel = ebenenschluessel(position, eltern)
  const achse = fest ?? aufriss.zustand.get(schluessel)
  // Ein Aufklappzustand überlebt den Wechsel von Periode und Bericht; was dort
  // eine Position nach Sachkonten war, kann hier eine Summe sein.
  if (!achse || (summe && achse === 'konten')) {
    return null
  }

  const letzte = eltern.at(-1) ?? { einheit: null, konto: null }
  const zusatz = zusatzachsen(quelle)
  const nach = katalog()
  const angebot = quelle?.angebot ?? []
  const einzug = einzugTiefe(ebeneTiefe, (eltern.length + 1) * AUFRISSSTUFE)

  // Die Zellen dieser Ebene: aus dem Vorrat geschnitten, oder nachgeladen.
  const stand: Ebenenstand<T> = istGrundachse(achse)
    ? { zustand: 'bereit', zellen: ebene(zellen, letzte, achse) }
    : quelle
      ? aufriss.ebeneVon<T>(quelle, position, eltern, achse)
      : { zustand: 'fehler' }

  // Eine Störung steht im Störungsfenster und nicht noch einmal hier (R1003).
  if (stand.zustand === 'fehler' && stand.satz === null) {
    return null
  }

  if (stand.zustand !== 'bereit') {
    return (
      <tr className="beitrag aufrissmeldung">
        {stand.zustand === 'laedt' && quelle && (
          <Anforderung anfordern={() => aufriss.anfordern(quelle, position, eltern, achse)} />
        )}
        {vorspalte}
        <td colSpan={spaltenzahl - (vorspalte ? 1 : 0)} style={{ paddingLeft: einzug }}>
          {stand.zustand === 'laedt' ? t('aufriss.laedt') : (stand.satz ?? t('aufriss.fehler'))}
        </td>
      </tr>
    )
  }

  // Unter einer nachgeladenen Zelle ist der Abruf der neue Vorrat: Er trägt
  // die Kreuzzellen mit Konto und Gesellschaft (R212), und darunter geht es
  // weiter wie im Bericht selbst.
  const vorrat = istGrundachse(achse) ? zellen : stand.zellen
  // Auch der Abruf wird geschnitten: Er bringt die Kreuzzellen für die
  // Ebenen darunter mit, und die gehören nicht in diese.
  const alle = istGrundachse(achse) ? stand.zellen : ebene(stand.zellen, letzte, achse)

  // **Die Nullzeilenunterdrueckung greift an der Achse dieser Ebene** (7.3
  // Abschnitt 11). Wer unter einem Konto nach Gesellschaften aufreisst und
  // die leeren Firmen nicht sehen will, meint die Firmen -- nicht das Konto,
  // unter dem sie stehen.
  const kinder = unterdrueckt(optionen, achse) ? alle.filter((k) => !istLeereZelle(k)) : alle

  if (kinder.length === 0 && fest !== undefined) {
    return null
  }

  if (kinder.length === 0) {
    return (
      <tr className="beitrag aufrissmeldung">
        {vorspalte}
        <td colSpan={spaltenzahl - (vorspalte ? 1 : 0)} style={{ paddingLeft: einzug }}>
          {t('aufriss.leer')}
        </td>
      </tr>
    )
  }

  return (
    <>
      {kinder.map((kind) => {
        const pfad = [...eltern, kind]
        const tiefer = aufriss.zustand.get(ebenenschluessel(position, pfad))
        // Was dieser Pfad noch nicht verbraucht hat — und ob darunter
        // überhaupt Zeilen stehen. Ein Pfeil, der ins Leere führt, ist
        // ärgerlicher als gar keiner.
        const moeglich = moeglicheAchsen(vorrat, pfad, zusatz).filter((a) => !summe || a !== 'konten')
        const naechste = tiefer ?? moeglich.find(istGrundachse) ?? moeglich[0]
        const kannTiefer = naechste !== undefined
        const id = zeilenId(position, pfad)
        const name = zellenname(kind, nach, achse)

        return (
          // Zeile und ihre Unterebene gehören zusammen in ein Fragment. Zwei
          // getrennte Durchläufe — erst alle Geschwister, dann alle
          // Unterebenen — hängten den Aufriss eines Kontos unter das *letzte*
          // Konto der Position statt unter sein eigenes.
          <Fragment key={zellenschluessel(kind)}>
            <tr
              id={id}
              className={`beitrag ${trefferklasse(treffer, id)}`.trim()}
              // Jede Aufrisszeile ist fokussierbar und trägt das Menü: Der
              // Wertenachweis und das Zuklappen bewirken dort immer etwas —
              // auch wenn keine Achse mehr offen ist (7.3, berichtigt R171a).
              tabIndex={0}
              onContextMenu={(e) => {
                e.preventDefault()
                const ort = menueort(e, e.currentTarget)
                aufMenue(ort.x, ort.y, position, eltern, kind, zellkontext(e))
              }}
            >
              {vorspalte}
              <td style={{ paddingLeft: einzug }}>
                {/* Eine Reihe wie im Zeilenkopf: Gekürzt wird der Name, nicht der Zusatz (R1724). */}
                <span className="kopfreihe">
                <Menueknopf
                  aufMenue={(x, y) => aufMenue(x, y, position, eltern, kind, {})}
                />
                {kannTiefer && (
                  <button
                    type="button"
                    className={`menueknopf ${tiefer && tiefer !== vorgabe ? 'abweichend' : ''}`}
                    aria-label={t('bericht.aufklappen')}
                    aria-expanded={tiefer !== undefined}
                    // Den betätigt auch der Doppelklick auf eine Zahl ohne Konto (R1032).
                    data-zeilenpfeil=""
                    // Linksklick klappt auf und zu, ohne Menü — in der Achse,
                    // die für diese Zeile zuletzt galt, sonst der ersten, die
                    // dieser Pfad noch übrig hat.
                    onClick={() => aufriss.umschalten(position, pfad, naechste!)}
                  >
                    {tiefer ? '▾' : '▸'}
                  </button>
                )}
                {!kannTiefer && <Pfeilplatz />}
                {/* Ein Konto, das hier endet, sagt bis wann (7.3 Abschnitt 33, R1724). */}
                <Namenszeile bis={achse === 'konten' ? kind.giltBis : null}>
                  <span className={istNichtZugeordnet(kind, achse) ? 'nichtzugeordnet' : undefined}>
                    {name}
                  </span>
                </Namenszeile>
                {tiefer && tiefer !== vorgabe && (
                  <span className="aufrissmarker">{markertext(tiefer, angebot)}</span>
                )}
                </span>
              </td>
              {werteSpalten(kind, id, pfad)}
            </tr>

            {/* Die nächste Stufe, unmittelbar unter ihrer Zeile. */}
            <Aufrisszeilen
              position={position}
              ebeneTiefe={ebeneTiefe}
              zellen={vorrat}
              eltern={pfad}
              aufriss={aufriss}
              quelle={quelle}
              vorgabe={vorgabe}
              spaltenzahl={spaltenzahl}
              treffer={treffer}
              aufMenue={aufMenue}
              werteSpalten={werteSpalten}
              vorspalte={vorspalte}
              summe={summe}
            />
          </Fragment>
        )
      })}
    </>
  )
}

/**
 * Die Punkte des Kontextmenüs an einer Zeile oder Aufrisszelle — **drei
 * Gruppen, in jedem Bericht dieselben** (7.3 Abschnitt 3), und darunter zwei,
 * die schreiben.
 *
 * 1. **Der Wertenachweis** dieser Zelle (R204). Er steht zuerst: Der Aufriss
 *    beantwortet „woraus besteht das", der Nachweis „wie ist es entstanden" —
 *    und die zweite Frage wird häufiger an eine auffällige Zahl gestellt.
 * 2. **Aufriss dieser Zeile:** die Achsen, die auf dem Pfad noch offen sind —
 *    der eingestellte Aufriss trägt ein Häkchen —, und das Zuklappen.
 * 3. **Aufriss im ganzen Bericht:** alles nach Sachkonten, alles nach
 *    Gesellschaften, alles zuklappen.
 * 3a. **Gliederungsebenen** (R445), wo der Bericht eine Gliederung hat.
 * 4. **Buchen** (R385) — schreibt in das Journal.
 * 5. **Anlegen** (R494) — schreibt in die Struktur.
 *
 * **Die drei Ansichtsgruppen stehen hinter je einem Punkt** (R576): Sie ändern
 * nur, was man sieht, und ein Klick mehr kostet dort nichts. **Die beiden
 * letzten bleiben flach, unten und abgesetzt**, weil ein Menü, in dem *Nach
 * Sachkonten aufreißen* und *Konto anlegen* nebeneinanderstehen, den
 * Unterschied zwischen Hinsehen und Eingreifen einebnet.
 *
 * Angeboten wird nur, was etwas bewirkt: Ein Menüpunkt, der nichts tut, ist
 * ein Versprechen, das beim Anklicken bricht.
 */
/** Die vier Elementtypen eines Berichts (2.1 Abschnitt 3.6, R1704). */
export type Elementtyp = 'position' | 'konto' | 'referenz' | 'summenzeile'

/** Der Löschpunkt heißt nach dem Element, auf das geklickt wurde (R1705). */
const LOESCHPUNKT: Record<Elementtyp, Sprachschluessel> = {
  position: 'anlage.menue.loeschen.position',
  konto: 'anlage.menue.loeschen.konto',
  referenz: 'anlage.menue.loeschen.referenz',
  summenzeile: 'anlage.menue.loeschen.summenzeile',
}

export function menuepunkte({
  offen,
  moeglich,
  angebot = [],
  aufAchse,
  aufZuklappen,
  aufHerkunft,
  aufBuchen,
  anlage,
  bericht,
  gliederung,
  aufReihenfolge,
  aufEigenschaften,
  ablage,
}: {
  offen: Achse | undefined
  moeglich: Achse[]
  angebot?: Achsenangabe[]
  aufAchse: (a: Achse) => void
  aufZuklappen: () => void
  aufHerkunft?: () => void
  /**
   * Die **vierte Gruppe** (R385) — sie steht ganz unten und abgesetzt, weil
   * sie als einzige etwas *schreibt* und deshalb nicht neben den
   * Ansichtsbefehlen stehen darf.
   */
  aufBuchen?: () => void
  /**
   * Die **fünfte Gruppe** (R494, 7.4 Abschnitt 14) — eine Struktur dort
   * anlegen, wo ihr Fehlen auffällt.
   *
   * **Angeboten wird nur, was die Zeile zulässt** (R495): An einer Summenzeile
   * steht nur die Unterposition, an einer Detailzeile mit Konten nur das
   * Konto. Wo beides fehlt, steht die Gruppe gar nicht da — auf einer
   * Aufrisszeile etwa, die keine Position ist, sondern ein Schnitt durch eine.
   */
  /*
    **Angelegt und gelöscht wird nach Elementtyp** (2.1 Abschnitt 3.6, R1704,
    R1705): *Neue Position …*, *Neues Konto …*, *Neue Referenz …*, *Neue
    Summenzeile …* an einer Position, und das Löschen heißt nach dem Element,
    auf das geklickt wurde. Bis zum 2026-10-14 hieß dasselbe Anlegen in der
    Kapitalflussrechnung *Neue Unterzeile …* und in der Bilanz *Neue
    Unterposition …*, und es gab *Neue Zeile …* für eine Zeile danach.
  */
  anlage?: {
    aufPosition?: () => void
    aufKonto?: () => void
    aufReferenz?: () => void
    aufSummenzeile?: () => void
    /**
     * Was auf diesem Element gelöscht wird — der Elementtyp gibt dem Punkt
     * seinen Namen. Was nicht gelöscht werden darf, sagt danach ein Fenster
     * mit dem Grund (R1707).
     */
    loeschen?: { art: Elementtyp; tun: () => void }
  }
  /** Die dritte Gruppe — wo ein Bericht sie hat. */
  bericht?: { aufAlle: (a: Achse) => void; aufZu: () => void }
  /**
   * Die Gliederung des ganzen Berichts (R445).
   *
   * **Sie trägt ihr Wort im Namen:** *Alles zuklappen* meint zwei Punkte
   * weiter oben den Aufriss (R280). Zwei verschiedene Dinge unter einem Wort
   * wären eine Falle, und zwar die, vor der R444 die Pfeile bewahrt.
   */
  gliederung?: {
    ebenen: number[]
    etwasZu: boolean
    aufEbene: (ebene: number) => void
    aufGanz: () => void
  }
  /**
   * **Ganz unten: die Eigenschaften der Zeile** (R857) — ihr Stammdatenblatt
   * als Fenster, gepflegt dort, wo die Zeile steht.
   *
   * **Zuletzt und hinter einer Linie**, wie man es aus jedem Kontextmenü
   * kennt: Der Punkt betrifft nicht die Zahl unter dem Zeiger, sondern das,
   * was die Zeile ist.
   */
  /**
   * **Reihenfolge ändern …** (7.4 Abschnitt 32, R1341) — am Element selbst,
   * nicht an seinem übergeordneten.
   */
  aufReihenfolge?: () => void
  aufEigenschaften?: () => void
  /** Ausschneiden an einer Kontozeile, Einfügen an einer Position (7.3 Abschnitt 31, R1642). */
  ablage?: { aufAusschneiden?: () => void; einfuegen?: { text: string; tun: () => void } }
}): Menuepunkt[] {
  const punkte: Menuepunkt[] = []

  if (aufHerkunft) {
    punkte.push({ schluessel: 'aufriss.menue.herkunft', tun: aufHerkunft }, { art: 'trenner' })
  }

  /*
    **Jede Ansichtsgruppe steht hinter einem Punkt** (R576, seit dem
    2026-09-16). Vorher standen sie flach untereinander: in der Bilanz
    fünfzehn Punkte über 569 Pixel, und *Konto darunter anlegen* lag unter dem
    Fensterrand — der Anwender sah die Funktion nicht, die er suchte.

    **Zusammengefasst wird nach Reichweite, nicht nach Zeilenzahl.** Die drei
    Gruppen beantworten drei verschiedene Fragen: *diese Zeile*, *jede Zeile*,
    *wie tief überhaupt*. Wer sie nach Länge zerteilte — die ersten vier Achsen
    oben, der Rest darunter —, hätte ein kürzeres Menü und keine Ordnung darin.

    **Was schreibt, bleibt flach** (R494): Buchen und Anlegen stehen weiter
    unten und einzeln da. Ein Untermenü nimmt den Punkt aus dem Blick, und
    genau das war der Befund, der hier abgestellt wird.
  */
  punkte.push({
    schluessel: 'aufriss.menue.zeile',
    unter: [
      ...moeglich.map<Menuepunkt>((a) => ({
        text: menuetext(a, angebot),
        wahl: true,
        gewaehlt: offen === a,
        tun: () => aufAchse(a),
      })),
      { schluessel: 'aufriss.menue.zuklappen', wenn: offen !== undefined, tun: aufZuklappen },
    ],
  })

  if (bericht) {
    punkte.push({
      schluessel: 'bericht.menue.alles',
      unter: [
        { schluessel: 'bericht.menue.alle.konten', tun: () => bericht.aufAlle('konten') },
        { schluessel: 'bericht.menue.alle.einheiten', tun: () => bericht.aufAlle('einheiten') },
        { schluessel: 'bericht.menue.zuklappen', tun: bericht.aufZu },
      ],
    })
  }

  // **Angeboten wird nur, was etwas bewirkt:** Wo die Gliederung zwei Ebenen
  // hat, gibt es nichts zusammenzuschieben, und *Ganze Gliederung zeigen*
  // steht nur da, solange etwas zu ist. Die Gruppe selbst fällt dann mit weg —
  // ein Punkt, der ein leeres Kästchen aufmacht, ist keiner (`bereinigt`).
  if (gliederung && (gliederung.ebenen.length > 0 || gliederung.etwasZu)) {
    punkte.push({
      schluessel: 'bericht.menue.gliederung',
      unter: [
        ...gliederung.ebenen.map<Menuepunkt>((e) => ({
          text: t('bericht.menue.gliederung.ebene', { ebene: String(e) }),
          tun: () => gliederung.aufEbene(e),
        })),
        {
          schluessel: 'bericht.menue.gliederung.ganz',
          wenn: gliederung.etwasZu,
          tun: gliederung.aufGanz,
        },
      ],
    })
  }

  // **Die vierte Gruppe, ganz unten** (R385): Was die Zelle weiß, steht
  // vorbelegt in der Maske — Bewertungsebene, Periode, Konto, Gesellschaft und die
  // aufgerissene Dimension. Alles änderbar; vorbelegt heißt nicht festgelegt.
  if (aufBuchen) {
    punkte.push({ art: 'trenner' }, { schluessel: 'bericht.menue.buchen', tun: aufBuchen })
  }

  // **Die fünfte Gruppe, darunter** (R494): Sie schreibt wie die vierte, aber
  // in die Struktur statt in das Journal — und deshalb steht sie nicht neben
  // ihr, sondern hinter einer eigenen Linie.
  if (anlage?.aufPosition || anlage?.aufKonto || anlage?.aufReferenz || anlage?.aufSummenzeile || anlage?.loeschen) {
    punkte.push(
      { art: 'trenner' },
      { schluessel: 'anlage.menue.position', wenn: Boolean(anlage.aufPosition), tun: () => anlage.aufPosition!() },
      { schluessel: 'anlage.menue.konto', wenn: Boolean(anlage.aufKonto), tun: () => anlage.aufKonto!() },
      { schluessel: 'anlage.menue.referenz', wenn: Boolean(anlage.aufReferenz), tun: () => anlage.aufReferenz!() },
      { schluessel: 'anlage.menue.summenzeile', wenn: Boolean(anlage.aufSummenzeile), tun: () => anlage.aufSummenzeile!() },
      {
        schluessel: LOESCHPUNKT[anlage.loeschen?.art ?? 'position'],
        wenn: Boolean(anlage.loeschen),
        tun: () => anlage.loeschen!.tun(),
      },
    )
  }

  // **Ausschneiden und Einfügen** (R1642): schreibt in die Struktur wie das
  // Anlegen und steht deshalb bei ihm, abgesetzt von den Ansichten.
  if (ablage?.aufAusschneiden || ablage?.einfuegen) {
    punkte.push(
      { art: 'trenner' },
      { schluessel: 'bericht.menue.ausschneiden', wenn: Boolean(ablage.aufAusschneiden), tun: () => ablage.aufAusschneiden!() },
      ...(ablage.einfuegen ? [{ text: ablage.einfuegen.text, tun: ablage.einfuegen.tun }] : []),
    )
  }

  if (aufReihenfolge) {
    punkte.push({ art: 'trenner' }, { schluessel: 'reihenfolge.menue', tun: aufReihenfolge })
  }

  if (aufEigenschaften) {
    punkte.push({ art: 'trenner' }, { schluessel: 'menue.eigenschaften', tun: aufEigenschaften })
  }

  return punkte
}

/** Die offenen Achsen einer Zeile, für das Menü — mit dem Angebot der Quelle. */
export function moeglichFuer(zellen: Zelle[], pfad: Zelle[], quelle: Achsenquelle | undefined): Achse[] {
  return moeglicheAchsen(zellen, pfad, zusatzachsen(quelle))
}

export { Zeilenmenue, VORGABE, offeneAchsen, achsenAusPfad }
